import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Col, Row, Container } from "react-bootstrap";
import { useRouter } from '../../util/router';
import { useUser } from '../../util/user';
import NotFoundPage from '../general/NotFound';

import { GetApp } from '@material-ui/icons';

import pdfMake, { getDocDefinition } from '../../util/pdf';
import htmlToPdfmake from 'html-to-pdfmake';
 
import styles from './Documentation.module.css';
import { useSite } from '../../util/sites';

export default function Documentation({ DocComponent, DocSideBarComponent, backRoutePath }) {
	// --- for restricting access ---
	const [hasAccess, setHasAccess] = useState(false);
  const router = useRouter();
  const userObj = useUser();
	useEffect(() => {
    // only gy users and client have access to this page
    if (userObj?.roles?.includes("_GY:Base") || userObj?.roles?.includes("_CLIENT:Base")) {
			setHasAccess(true);
    } else {
			router.push("/solar/logout");
		}
  }, [router, userObj]);
	// ------------------------------

	const currentSite = useSite();
	useEffect(() => {
		if(currentSite.showSelect) { currentSite.showSelectSites(false) }
	}, [ currentSite ])

	const innerRef = useRef(null);

	const recursivelyModifyNodes = useCallback(nodes => {
		// filter out empty node
		const _nodes = nodes.filter(_node => !(typeof(_node.text) === 'string' && !_node.text.trim()));
		for(let _node of _nodes) {
			for(let key in _node) {
				if(_node[key] instanceof Array) { // node has child nodes
					_node[key] = recursivelyModifyNodes(_node[key]);
				}
			}
			if(_node.type === 'line') { // modify sytle of horizontal line
				_node.lineColor = "#eeeeee";
				_node.lineWidth = 0.25;
			}
		}
		return _nodes;
	}, []);

	// --- listen when all markdowns loaded ---
	const [loadedMarkdowns, setLoadedMarkdowns] = useState([]);
	const allMarkdownsLoaded = loadedMarkdowns.length === innerRef.current?.querySelectorAll('.markdown-wrapper').length;
	// scroll page according to the hash in the URL (#)
	const scrollPageToElm = useCallback(() => {
		const hash = router.location.hash;
		if(!hash) { return }
		const elmId = hash.match(/#[0-9a-zA-Z\-_:.]+/)?.[0];
		if(!elmId) { return }
		const scrollToElm = document.querySelector(elmId)
		if(!scrollToElm) { return }
		scrollToElm.scrollIntoView();
	}, [router]);
	const onMarkdownLoaded = useCallback(markdownUrl => {
		if(loadedMarkdowns.includes(markdownUrl)) { return }
		const newLoadedMarkdowns = [...loadedMarkdowns];
		newLoadedMarkdowns.push(markdownUrl);
		setLoadedMarkdowns(newLoadedMarkdowns);
		if(newLoadedMarkdowns.length === innerRef.current?.querySelectorAll('.markdown-wrapper').length) {
			// auto add ID in headings for navigation usign this pattern: `${markdownWrapperId}-${headingTagName}-${dashedHeadingText}`
			innerRef.current.querySelectorAll('.markdown-wrapper').forEach(markdownWrapper => markdownWrapper.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach(elm => { elm.id = `${markdownWrapper.id}-${elm.localName}-${elm.innerText.toLowerCase().trim().replaceAll(/[^a-zA-Z0-9\-_:.]+/g, ' ').trim().replaceAll(/\s+/g, '-')}` }));
			// scroll page to element according to hash (#) in URL
			setTimeout(scrollPageToElm);
		}
	}, [ loadedMarkdowns, scrollPageToElm ]);
	/// ---------------------------------------

	// download PDF
	const downloadPdf = useCallback(() => {
		const innerElm = innerRef.current;
		// set specific number of maxWidth in pixel as PDFMake doesn't understand % (percent)
		innerElm.querySelectorAll('img').forEach(elm => { elm.style.maxWidth = '650px'; elm.style.maxHeight = '300px' });
		let nodes = htmlToPdfmake(innerElm.innerHTML);
		nodes = recursivelyModifyNodes(nodes);
		pdfMake.createPdf(getDocDefinition({ content: nodes })).download();
		// set the unit of maxWidth back to % (percent)
		innerElm.querySelectorAll('img').forEach(elm => { elm.style.maxWidth = '100%'; elm.style.maxHeight = 'auto' });
	}, [ innerRef, recursivelyModifyNodes]);

	// handle back button click: go to previous route using react router
	const handleBackBtnClick = useCallback(() => {
		router.history.replace(backRoutePath);
	}, [ router.history, backRoutePath ]);

	return hasAccess ? (
		<div className={styles.outer}>
			<Container>
				<Row className={styles.row}>
					<Col xs={12} sm={12} md={8}>
						{allMarkdownsLoaded && <button onClick={handleBackBtnClick} className={`btn btn-default ${styles.backBtn}`} type="button">
							<span className="glyphicon glyphicon-arrow-left"></span> Back
						</button>}
						<div className={`${styles.inner} ${!allMarkdownsLoaded ? styles.hidden : ''}`} ref={innerRef}>
							<DocComponent onMarkdownLoaded={onMarkdownLoaded} />
						</div>
						{!allMarkdownsLoaded && <div>Loading...</div>}
					</Col>
					<Col xs={12} sm={12} md={4} className="hidden-sm">
						<div className={styles.stickySidebar}>
							<DocSideBarComponent innerRef={innerRef} loadedMarkdowns={loadedMarkdowns} />
						</div>
					</Col>
				</Row>
			</Container>
			<GetApp className={styles.btnDownload} onClick={downloadPdf} />
		</div>) : (
		<NotFoundPage />
	)
}